import { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { DeleteForever } from "@mui/icons-material";
import { FileUploader } from "react-drag-drop-files";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function SupplyImagePicker({
  supplyId,
  imageNumber,
  pathValue,
  readOnly,
  hideUploader,
  hideDelete,
  onView,
  onReload,
}) {
  const { SUPPLY_API, config } = useGlobalContext();
  const [dataUrl, setDataUrl] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [uploaderKey, setUploaderKey] = useState(0);

  useEffect(() => {
    let active = true;
    let objectUrl;
    const loadImage = async () => {
      if (!pathValue || `${pathValue}`.trim().length === 0) {
        setDataUrl(undefined);
        return;
      }
      setLoading(true);
      try {
        // Download the same way the evidences viewer does: hit the backend
        // download endpoint as a blob and build an object URL for display.
        const res = await axios({
          ...config,
          method: "get",
          responseType: "blob",
          url: `${SUPPLY_API}/${supplyId}/image/${imageNumber}/download`,
        });
        objectUrl = URL.createObjectURL(res.data);
        if (active) {
          setDataUrl(objectUrl);
        }
      } catch (error) {
        if (active) {
          setDataUrl(undefined);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    loadImage();
    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // eslint-disable-next-line
  }, [pathValue, supplyId]);

  const readFileAsDataUrl = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    });

  const handleUpload = async (file) => {
    if (!file) {
      return;
    }
    setErrorMessage(undefined);
    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("document", file, file.name);
      const requestConfig = {
        ...config,
        headers: { ...config.headers, "Content-Type": undefined },
      };
      const result = await axios.post(
        `${SUPPLY_API}/${supplyId}/image/${imageNumber}`,
        formData,
        requestConfig,
      );
      if (result?.data?.Ok === false || result?.data?.ok === false) {
        setErrorMessage(
          result?.data?.Data || result?.data?.data || localize.error_unexpected,
        );
        return;
      }
      const preview = await readFileAsDataUrl(file);
      setDataUrl(preview);
      if (onReload) {
        await onReload();
      }
    } catch (error) {
      setErrorMessage(error?.message ?? localize.error_unexpected);
    } finally {
      setBusy(false);
      // Reset the drag & drop back to its initial state/label (removes "Cargando").
      setUploaderKey((prev) => prev + 1);
    }
  };

  const handleDelete = async () => {
    setErrorMessage(undefined);
    setBusy(true);
    try {
      const result = await axios.delete(
        `${SUPPLY_API}/${supplyId}/image/${imageNumber}`,
        config,
      );
      if (result?.data?.Ok === false || result?.data?.ok === false) {
        setErrorMessage(
          result?.data?.Data || result?.data?.data || localize.error_unexpected,
        );
        return;
      }
      setDataUrl(undefined);
      if (onReload) {
        await onReload();
      }
    } catch (error) {
      setErrorMessage(error?.message ?? localize.error_unexpected);
    } finally {
      setBusy(false);
    }
  };

  const renderUploader = () =>
    busy ? (
      <Box
        className="flex items-center justify-center gap-2 w-full"
        sx={{ py: 2 }}
      >
        <CircularProgress size={20} sx={{ color: "var(--app-bg, #10366E)" }} />
        <span className="text-sm">{localize.uploading_image}</span>
      </Box>
    ) : (
      <FileUploader
        key={uploaderKey}
        classes="w-full"
        name="file"
        types={["png"]}
        label={localize.put_supply_image_here}
        handleChange={handleUpload}
        hoverTitle={localize.drop_files_here}
        uploadedLabel={localize.loading}
      />
    );

  const showUploader = !readOnly && !hideUploader;
  const showDelete = !readOnly && !hideDelete;

  return (
    <Box className="flex flex-col gap-2 w-full">
      {loading ? (
        <Box className="flex justify-center items-center" sx={{ py: 2 }}>
          <CircularProgress size={28} sx={{ color: "var(--app-bg, #10366E)" }} />
        </Box>
      ) : dataUrl ? (
        <Box className="flex flex-row gap-3 items-start w-full" sx={{ minWidth: 0 }}>
          <img
            src={dataUrl}
            alt={`${localize.images} ${imageNumber}`}
            onClick={() => onView && onView(dataUrl)}
            style={{
              cursor: "pointer",
              maxHeight: "110px",
              maxWidth: "160px",
              objectFit: "contain",
              borderRadius: "6px",
              border: "1px solid #E2E8F0",
            }}
          />
          {(showUploader || showDelete) && (
            <Box className="flex flex-col gap-2" sx={{ flex: 1, minWidth: 0 }}>
              {showUploader && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    "& label": {
                      minWidth: "0 !important",
                      width: "100% !important",
                      maxWidth: "100% !important",
                      boxSizing: "border-box",
                    },
                  }}
                >
                  {renderUploader()}
                </Box>
              )}
              {showDelete && (
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  type="button"
                  disabled={busy}
                  onClick={handleDelete}
                  style={{ fontSize: "0.8em" }}
                >
                  <DeleteForever style={{ width: "18px", height: "18px" }} />
                  {localize.delete}
                </Button>
              )}
            </Box>
          )}
        </Box>
      ) : (
        showUploader && (
          <Box
            className="w-full"
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              "& label": {
                minWidth: "0 !important",
                width: "100% !important",
                maxWidth: "100% !important",
                boxSizing: "border-box",
              },
            }}
          >
            {renderUploader()}
          </Box>
        )
      )}

      {errorMessage && (
        <Alert severity="error" className="w-full">
          <span className="text-xs">{`${errorMessage}`}</span>
        </Alert>
      )}
    </Box>
  );
}
